/**
 * Chat Component
 *
 * Displays room messages including text, voice, POI, and system messages.
 */

import { useRef, useEffect, useState, useCallback } from 'react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import type { Role } from '@/lib/types'
import type { MessageEntry } from '@/lib/sync'
import { getConfidenceIndicator } from '@/lib/types'
import { displayAction, isEnforcementAction } from '@/lib/features'
import { playAudio, stopAudio, formatTime } from '@/lib/audio'

interface ChatProps {
  messages: MessageEntry[]
  myPeerId: string
  className?: string
  autoPlayVoice?: boolean
}

export function Chat({ messages, myPeerId, className, autoPlayVoice = true }: ChatProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const lastMessageCountRef = useRef(messages.length)
  const playedMessagesRef = useRef<Set<string>>(new Set())

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [messages.length])

  // Helper to play a voice message
  const playVoiceMessage = useCallback(async (message: MessageEntry) => {
    if (!message.audioData) return

    // Convert base64 to Uint8Array
    const binary = atob(message.audioData)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }

    setPlayingId(message.id)

    try {
      await playAudio(bytes, {
        onEnded: () => setPlayingId(null),
        onError: () => setPlayingId(null),
      })
    } catch {
      setPlayingId(null)
    }
  }, [])

  // Queue for auto-playing voice messages
  const playQueueRef = useRef<MessageEntry[]>([])
  const isProcessingQueueRef = useRef(false)

  // Process the play queue
  const processPlayQueue = useCallback(async () => {
    if (isProcessingQueueRef.current || playQueueRef.current.length === 0) return

    isProcessingQueueRef.current = true
    const nextMessage = playQueueRef.current.shift()

    if (nextMessage && nextMessage.audioData) {
      try {
        await playVoiceMessage(nextMessage)
      } catch (err) {
        console.error('Failed to play voice message:', err)
      }
    }

    isProcessingQueueRef.current = false

    // Process next message after a small delay
    if (playQueueRef.current.length > 0) {
      setTimeout(processPlayQueue, 200)
    }
  }, [playVoiceMessage])

  // Auto-play incoming voice messages (not from self)
  useEffect(() => {
    if (!autoPlayVoice) return
    if (messages.length <= lastMessageCountRef.current) {
      lastMessageCountRef.current = messages.length
      return
    }

    // Check for new voice messages
    const newMessages = messages.slice(lastMessageCountRef.current)
    lastMessageCountRef.current = messages.length

    for (const msg of newMessages) {
      // Only auto-play voice messages from others that we haven't played
      if (
        msg.type === 'voice' &&
        msg.senderId !== myPeerId &&
        msg.audioData &&
        !playedMessagesRef.current.has(msg.id)
      ) {
        // Mark as played immediately to prevent duplicates
        playedMessagesRef.current.add(msg.id)
        // Add to queue (copy the message to avoid reference issues)
        playQueueRef.current.push({ ...msg })
      }
    }

    // Start processing queue if not already running
    if (playQueueRef.current.length > 0 && !isProcessingQueueRef.current) {
      setTimeout(processPlayQueue, 100)
    }
  }, [messages, myPeerId, autoPlayVoice, processPlayQueue])

  const handlePlayVoice = async (message: MessageEntry) => {
    if (playingId === message.id) {
      stopAudio()
      setPlayingId(null)
      return
    }

    playVoiceMessage(message)
  }

  return (
    <div
      ref={containerRef}
      className={clsx(
        'flex-1 overflow-y-auto p-3 space-y-3',
        className
      )}
    >
      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          isOwn={message.senderId === myPeerId}
          isPlaying={playingId === message.id}
          onPlayVoice={() => handlePlayVoice(message)}
        />
      ))}

      {messages.length === 0 && (
        <div className="text-center text-white/40 py-8">
          No messages yet. Start the conversation!
        </div>
      )}
    </div>
  )
}

interface MessageBubbleProps {
  message: MessageEntry
  isOwn: boolean
  isPlaying?: boolean
  onPlayVoice?: () => void
}

const CornerAccents = ({ colorClass = 'border-primary', position = 'right' }: { colorClass?: string, position?: 'left' | 'right' }) => (
  <>
    <div className={clsx(
      "absolute top-0 w-2 h-2 border-t-2",
      position === 'right' ? "border-r-2 -right-[2px]" : "border-l-2 -left-[2px]",
      colorClass
    )}></div>
    <div className={clsx(
      "absolute bottom-0 w-2 h-2 border-b-2",
      position === 'right' ? "border-r-2 -right-[2px]" : "border-l-2 -left-[2px]",
      colorClass
    )}></div>
  </>
)

function MessageBubble({
  message,
  isOwn,
  isPlaying = false,
  onPlayVoice,
}: MessageBubbleProps) {
  const timestamp = formatDistanceToNow(new Date(message.timestamp), {
    addSuffix: true,
  })

  // System messages
  if (message.type === 'system') {
    return (
      <div className="flex justify-center my-2">
        <div className="w-full bg-white/5 flex items-center justify-center py-1 border-t border-b border-white/10 relative">
          <span className="text-[9px] text-white/60 font-mono font-bold tracking-[0.3em] uppercase opacity-70">
            // {message.content} //
          </span>
        </div>
      </div>
    )
  }

  // POI messages (Feed Cards)
  if (message.type === 'poi' && message.poiType) {
    const action = displayAction(message.poiType)
    // Hidden report types (App Store build) take the neutral "Alert" styling.
    const styleKey = isEnforcementAction(message.poiType) ? 'need_help' : action.key
    const colorClass = styleKey === 'need_help' ? 'text-yellow-500' :
      styleKey === 'blocked_route' ? 'text-orange-500' :
        styleKey === 'regroup' ? 'text-toxic' :
          styleKey === 'police' ? 'text-alert' : 'text-primary'

    // Gradient background based on type
    const bgGradient = styleKey === 'need_help' ? 'from-yellow-900/40' :
      styleKey === 'blocked_route' ? 'from-orange-900/40' :
        styleKey === 'regroup' ? 'from-toxic/20' :
          styleKey === 'police' ? 'from-alert/20' : 'from-primary/20'

    const borderColor = styleKey === 'need_help' ? 'border-yellow-500' :
      styleKey === 'blocked_route' ? 'border-orange-500' :
        styleKey === 'regroup' ? 'border-toxic' :
          styleKey === 'police' ? 'border-alert' : 'border-primary'

    return (
      <div className="flex flex-col group animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className={clsx(
          "border-l-4 bg-gradient-to-r to-transparent pl-3 py-2 pr-2 relative overflow-hidden backdrop-blur-sm",
          borderColor,
          bgGradient
        )}>
          {/* Scanlines overlay */}
          <div className="absolute inset-0 scanlines opacity-20 pointer-events-none"></div>

          <div className="absolute top-0 right-0 p-1 opacity-20">
            <span className={clsx("material-icons-round text-4xl", colorClass)}>
              priority_high
            </span>
          </div>
          <div className="flex items-center space-x-2 mb-1 relative z-10">
            <span className={clsx("font-display font-bold text-xs uppercase tracking-widest flex items-center stroke-text", colorClass)}>
              <span className="material-icons-round text-sm mr-1">
                {action.emoji && action.emoji.length > 2 ? 'stars' : 'place'}
              </span>
              {action.label}
            </span>
          </div>
          <div className="relative z-10">
            <POIMessageContent message={message} colorClass={colorClass} />
          </div>

          <div className={clsx("flex items-center justify-between text-[9px] font-mono border-t pt-1 opacity-70 relative z-10 mt-1", colorClass.replace('text-', 'text-gray-400 border-'))}>
            <div className="flex items-center space-x-2">
              <span className="text-white/50 uppercase">{message.senderName}</span>
              <RoleBadge role={message.senderRole} />
            </div>
            <span>{timestamp}</span>
          </div>
        </div>
      </div>
    )
  }

  // Voice messages
  if (message.type === 'voice') {
    return (
      <div className={clsx('flex flex-col gap-1', isOwn ? 'items-end' : 'items-start')}>
        <div className={clsx(
          "relative px-4 py-3 shadow-[0_4px_20px_rgba(0,0,0,0.5)] transition-colors group min-w-[220px]",
          isOwn ?
            "bg-black/60 border-l-2 border-primary border-r border-t border-b border-white/10 rounded-r-lg rounded-tl-none backdrop-blur-md" :
            "bg-black/60 border-l-2 border-secondary border-r border-t border-b border-white/10 rounded-r-lg rounded-tl-none backdrop-blur-md"
        )}>
          {/* Tactical Label */}
          <div className={clsx(
            "absolute -top-3 left-0 text-[8px] font-mono font-bold uppercase tracking-widest px-1",
            isOwn ? "text-primary bg-primary/10 border border-primary/20" : "text-secondary bg-secondary/10 border border-secondary/20"
          )}>
            VOICE_DATA
          </div>

          <VoiceMessageContent
            message={message}
            isPlaying={isPlaying}
            onPlay={onPlayVoice}
            isOwn={isOwn}
          />
        </div>

        <div className={clsx(
          'flex items-center gap-2 text-[8px] text-gray-500 font-mono tracking-wider',
          isOwn && 'flex-row-reverse'
        )}>
          <span className="text-white/30">{timestamp}</span>
          <span className={isOwn ? "text-primary/50 font-bold" : "text-secondary/50 font-bold"}> :: </span>
          <span className="uppercase text-white/40">{message.senderName}</span>
          <RoleBadge role={message.senderRole} />
        </div>
      </div>
    )
  }

  // Text messages (Standard Chat)
  return (
    <div className={clsx('flex flex-col gap-1', isOwn ? 'items-end' : 'items-start')}>
      <div className={clsx(
        "px-4 py-3 max-w-[85%] relative shadow-[0_4px_15px_rgba(0,0,0,0.3)] backdrop-blur-sm",
        isOwn ?
          "bg-primary/20 border border-primary/50 text-white rounded-lg rounded-tr-none" :
          "bg-white/5 border border-white/10 text-white/90 rounded-lg rounded-tl-none"
      )}>
        {/* Helper accent for Others */}
        {!isOwn && (
          <CornerAccents position="left" colorClass="border-white/20" />
        )}
        {isOwn && (
          <CornerAccents position="right" colorClass="border-primary/50" />
        )}

        <p className="text-sm font-sans leading-relaxed">{message.content}</p>
      </div>

      <div className={clsx(
        'flex items-center gap-2 text-[8px] text-gray-500 font-mono tracking-wider',
        isOwn && 'flex-row-reverse'
      )}>
        <span className="text-white/30">{timestamp}</span>
        <span className="text-primary/50 font-bold"> :: </span>
        <span className="uppercase text-white/40">{message.senderName}</span>
        <RoleBadge role={message.senderRole} />
      </div>
    </div>
  )
}

function POIMessageContent({ message, colorClass }: { message: MessageEntry, colorClass: string }) {
  const vehicleData = message.vehicleData

  // Vehicle sighting with extra data (never shown where the Vehicles module is off)
  if (message.poiType === 'vehicle_sighting' && vehicleData && !isEnforcementAction('vehicle_sighting')) {
    return (
      <div className="space-y-2">
        {/* Vehicle info row */}
        <div className="flex flex-wrap gap-2 items-center">
          {vehicleData.plate && (
            <span className="px-2 py-0.5 bg-yellow-500/20 border border-yellow-500/50 rounded text-yellow-400 font-mono font-bold text-xs tracking-wider">
              {vehicleData.plate}
              {vehicleData.plateConfidence === 'partial' && <span className="ml-1 opacity-60">~</span>}
            </span>
          )}
          {vehicleData.vehicleType && (
            <span className="text-white/60 text-xs">
              {vehicleData.vehicleType === 'suv' ? '🚙 SUV' :
               vehicleData.vehicleType === 'sedan' ? '🚗 Sedan' :
               vehicleData.vehicleType === 'van' ? '🚐 Van' :
               vehicleData.vehicleType === 'truck' ? '🛻 Truck' :
               vehicleData.vehicleType === 'unmarked' ? '🚘 Unmarked' : '🚗 Vehicle'}
            </span>
          )}
          {vehicleData.vehicleColor && (
            <span className="text-white/50 text-xs capitalize">{vehicleData.vehicleColor}</span>
          )}
          {vehicleData.direction && (
            <span className="text-white/50 text-xs">
              → {vehicleData.direction === 'stationary' ? '⊙' : vehicleData.direction}
            </span>
          )}
          {vehicleData.isFollowing && (
            <span className="px-1.5 py-0.5 bg-red-500/20 border border-red-500/50 rounded text-red-400 text-[10px] font-bold uppercase">
              Following
            </span>
          )}
        </div>

        {/* Description */}
        {vehicleData.description && (
          <p className="text-white/60 text-xs">{vehicleData.description}</p>
        )}

        {/* Photos */}
        {vehicleData.photos && vehicleData.photos.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {vehicleData.photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <img
                  src={photo.thumbnailDataUrl}
                  alt="Vehicle"
                  className="w-14 h-14 object-cover rounded border border-white/20 cursor-pointer hover:border-primary/50 transition-colors"
                />
                {/* Indicators */}
                <div className="absolute bottom-0.5 left-0.5 flex gap-0.5">
                  {photo.hasLocation && (
                    <span className="w-4 h-4 bg-black/70 rounded flex items-center justify-center text-[8px] text-green-400">📍</span>
                  )}
                  {photo.plateOCR && photo.plateOCR.confidence > 0.5 && (
                    <span className="w-4 h-4 bg-black/70 rounded flex items-center justify-center text-[8px] text-yellow-400">🚗</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Location */}
        <div className="text-white/50 text-[10px] font-mono flex items-center bg-black/30 p-1.5 rounded border border-white/5">
          {message.address ? (
            <>
              <span className={clsx("mr-2 opacity-70", colorClass)}>📍</span> {message.address}
            </>
          ) : (message.locationLat && message.locationLng) ? (
            <>
              <span className={clsx("mr-2 opacity-70", colorClass)}>📍</span>
              {message.locationLat.toFixed(5)}, {message.locationLng.toFixed(5)}
            </>
          ) : null}
        </div>
      </div>
    )
  }

  // Standard POI - Address / Coords for the Feed Card
  return (
    <div className="text-white/70 text-[10px] font-mono mb-2 flex items-center bg-black/30 p-1.5 rounded border border-white/5">
      {message.address ? (
        <>
          <span className={clsx("mr-2 opacity-70", colorClass)}>📍</span> {message.address}
        </>
      ) : (message.locationLat && message.locationLng) ? (
        <>
          <span className={clsx("mr-2 opacity-70", colorClass)}>📍</span>
          {message.locationLat.toFixed(5)}, {message.locationLng.toFixed(5)}
        </>
      ) : null}
    </div>
  )
}

function VoiceMessageContent({
  message,
  isPlaying,
  onPlay,
  isOwn
}: {
  message: MessageEntry
  isPlaying: boolean
  onPlay?: () => void
  isOwn: boolean
}) {
  const accentColor = isOwn ? 'text-primary' : 'text-secondary'
  const bgColor = isOwn ? 'bg-primary' : 'bg-secondary'
  const borderColor = isOwn ? 'border-primary' : 'border-secondary'

  return (
    <div className="flex items-center space-x-4 min-w-[200px]">
      <button
        onClick={onPlay}
        className={clsx(
          'w-8 h-8 flex items-center justify-center rounded transition-all border',
          isOwn ? "bg-primary/10 text-primary border-primary/30 hover:bg-primary/20" : "bg-secondary/10 text-secondary border-secondary/30 hover:bg-secondary/20",
          isPlaying && "animate-pulse shadow-[0_0_10px_currentColor]"
        )}
      >
        <span className="material-icons-round text-lg">{isPlaying ? 'stop' : 'play_arrow'}</span>
      </button>

      <div className="flex-1 h-8 flex flex-col justify-center space-y-1.5">
        {/* Fake waveform visual */}
        <div className={clsx("w-full h-[3px] bg-black/50 overflow-hidden relative border rounded-sm transition-colors", borderColor)}>
          <div className={clsx("absolute inset-0 opacity-20", bgColor)}></div>
          <div
            className={clsx("h-full w-1/3 relative shadow-[0_0_5px_currentColor]", bgColor, accentColor)}
            style={{ width: isPlaying ? '100%' : '33%', transition: 'width 10s linear' }} // primitive anim
          ></div>
          {/* Grid lines */}
          <div className="absolute inset-0 flex justify-between px-1">
            <div className="w-px h-full bg-black/50"></div>
            <div className="w-px h-full bg-black/50"></div>
            <div className="w-px h-full bg-black/50"></div>
          </div>
        </div>

        <div className="flex justify-between items-center w-full">
          <span className={clsx("text-[8px] font-mono font-bold", accentColor)}>
            {message.duration ? formatTime(message.duration) : '0:00'}
          </span>
          {/* Confidence */}
          {message.transcriptConfidence !== undefined && (
            <span className="text-[8px] text-white/30 font-mono tracking-tight bg-white/5 px-1 rounded">
              CONF: {getConfidenceIndicator(message.transcriptConfidence)}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

function RoleBadge({ role }: { role: Role }) {
  if (role === 'participant') return null

  return (
    <span
      className={clsx(
        'px-1 py-0.5 rounded-sm uppercase font-bold text-[8px] font-mono tracking-wider',
        role === 'organizer' && 'bg-primary/20 text-primary border border-primary/30',
        role === 'coordinator' && 'bg-teal/20 text-teal border border-teal/30'
      )}
    >
      {role === 'organizer' ? 'CMD' : 'OPS'}
    </span>
  )
}
